import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Footer from '../../components/ui/Footer';
import '../../pages/landing-page/landing.css';

const navLinks = [
  { href: '/#features', label: 'Product' },
  { href: '/#how-it-works', label: 'Process' },
  { href: '/#faq', label: 'FAQ' },
];

const LegalPageLayout = ({ document: legalDocument }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    if (isDarkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDark(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDark(true);
    }
  };

  return (
    <div className="landing-page flex min-h-screen flex-col bg-[var(--lp-bg)] text-[var(--lp-ink)] transition-colors duration-300">
      <header
        className={`fixed left-0 right-0 top-0 z-[100] transition-all duration-300 ${
          scrolled
            ? 'border-b border-[var(--lp-line)] bg-[var(--lp-bg)]/90 backdrop-blur-md'
            : 'bg-[var(--lp-bg)]/80 backdrop-blur-sm'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
          <Link to="/" className="text-xl font-semibold tracking-tight text-[var(--lp-ink)]">
            prepx<span className="text-[var(--lp-accent)]">ai</span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-mono text-[11px] font-semibold tracking-wider text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
              aria-label="Toggle theme"
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={16} />
            </button>

            <a
              href="/#waitlist"
              className="hidden h-9 items-center rounded-full bg-[var(--lp-accent)] px-5 text-xs font-semibold text-white transition-colors hover:bg-[var(--lp-accent)]/90 sm:inline-flex"
            >
              Join waitlist
            </a>

            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] md:hidden"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label="Menu"
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={18} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-[var(--lp-line)] bg-[var(--lp-bg)] px-5 py-4 md:hidden">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/#waitlist"
                onClick={() => setMobileOpen(false)}
                className="mt-2 flex h-10 items-center justify-center rounded-full bg-[var(--lp-accent)] text-sm font-semibold text-white hover:bg-[var(--lp-accent)]/90"
              >
                Join waitlist
              </a>
            </nav>
          </div>
        )}
      </header>

      <div className="relative overflow-hidden border-b border-[var(--lp-line)] bg-[var(--lp-surface)] pb-16 pt-32">
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
          <p className="mb-4 text-xs font-black tracking-widest text-[var(--lp-accent)]">
            {legalDocument.eyebrow}
          </p>
          <h1 className="mb-4 text-4xl font-black tracking-tight text-[var(--lp-ink)]">
            {legalDocument.title}
          </h1>
          <p className="mb-5 text-sm font-bold tracking-widest text-[var(--lp-accent)]">
            Last updated: {legalDocument.lastUpdated}
          </p>
          <p className="mx-auto max-w-3xl text-sm font-medium leading-6 text-[var(--lp-ink-soft)]">
            {legalDocument.summary}
          </p>
        </div>
      </div>

      <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-16">
        <div className="prose prose-slate max-w-none prose-sm dark:prose-invert">
          {legalDocument.sections.map((section) => (
            <section key={section.heading} className="mb-10">
              <h2 className="mb-4 text-xl font-semibold text-[var(--lp-ink)]">
                {section.heading}
              </h2>

              {section.paragraphs?.map((paragraph, index) => (
                <p key={`p-${index}`} className="mb-3 leading-relaxed text-[var(--lp-ink-soft)]">
                  {paragraph}
                </p>
              ))}

              {section.bullets && (
                <ul className="mb-4 list-disc space-y-2 pl-6">
                  {section.bullets.map((item, index) => (
                    <li key={`b-${index}`} className="leading-relaxed text-[var(--lp-ink-soft)]">
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {section.quote && (
                <blockquote className="my-4 rounded-r-xl border-l-4 border-[var(--lp-accent)] bg-emerald-50/60 px-5 py-4 dark:bg-emerald-950/20">
                  <p className="m-0 text-sm font-semibold leading-relaxed text-[var(--lp-ink)]">
                    [ ] {section.quote}
                  </p>
                </blockquote>
              )}

              {section.closingParagraphs?.map((paragraph, index) => (
                <p key={`cp-${index}`} className="mb-3 leading-relaxed text-[var(--lp-ink-soft)]">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPageLayout;
